import { DocumentActions } from '@/components/DocumentActions'
import AddIcon from '@mui/icons-material/Add'
import {
  Button,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { SendDocumentIcon } from '@/components/SendDocument'
import EditIcon from '@mui/icons-material/Edit'
import { useInvoices, useIssueInvoice } from '@/api/hooks'
import type { Invoice } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import {
  CancelInvoiceDialog,
  EditInvoiceDialog,
  NewInvoiceDialog,
} from '@/pages/billing/InvoiceDialogs'
import { EInvoiceChip, EInvoiceDialog } from '@/pages/billing/EInvoiceDialog'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatMoney,
  humanise,
  isoDate,
  PageHeader,
  StatusChip,
} from '@/components/common'

const STATUSES = ['DRAFT', 'ISSUED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'CANCELLED']
const INVOICE_TYPES = [
  'ADVANCE_REQUEST',
  'PROFORMA',
  'TAX_INVOICE',
  'SUPPLEMENTARY',
  'COMPLIMENTARY',
  'CANCELLATION_CHARGE',
  'FINAL_SETTLEMENT',
]

export function InvoicesPage() {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const hasRole = useAuthStore((state) => state.hasRole)
  const isSuperAdmin = hasRole('SUPER_ADMIN')
  const issue = useIssueInvoice()
  const [newOpen, setNewOpen] = useState(false)
  const [cancelling, setCancelling] = useState<Invoice | null>(null)
  const [editing, setEditing] = useState<Invoice | null>(null)
  const [eInvoicing, setEInvoicing] = useState<Invoice | null>(null)
  const [status, setStatus] = useState('')
  const [invoiceType, setInvoiceType] = useState('')
  // Opens on today. Every list and every report starts on the day being
  // worked, and the range is widened when somebody wants history.
  const [from, setFrom] = useState(() => isoDate(0))
  const [to, setTo] = useState(() => isoDate(0))
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)

  const { data, isLoading, error } = useInvoices({
    status: status || undefined,
    invoiceType: invoiceType || undefined,
    from,
    to,
    page,
    size,
  })

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle="Advance requests, tax invoices, supplementary bills and credit notes"
        actions={
          canWrite('billing') ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>
              New invoice
            </Button>
          ) : null
        }
      />
      <ErrorBanner error={error} />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Type"
              value={invoiceType}
              onChange={(event) => {
                setInvoiceType(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All types</MenuItem>
              {INVOICE_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="From"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </CardContent>
      </Card>

      <Card>
        {isLoading && <LinearProgress />}
        {data && data.content.length === 0 ? (
          <EmptyState message="No invoices in this window." />
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell align="right">Taxable</TableCell>
                  <TableCell align="right">Tax</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>E-invoice</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.content.map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell>{invoice.invoiceNumber}</TableCell>
                    <TableCell>
                      <Typography variant="caption">{humanise(invoice.invoiceType)}</Typography>
                    </TableCell>
                    <TableCell>{invoice.customerName}</TableCell>
                    <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                    <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                    <TableCell align="right">{formatMoney(invoice.taxableAmount)}</TableCell>
                    <TableCell align="right">{formatMoney(invoice.taxAmount)}</TableCell>
                    <TableCell align="right">{formatMoney(invoice.totalAmount)}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={invoice.balanceAmount > 0 ? 'warning.main' : 'success.main'}
                      >
                        {formatMoney(invoice.balanceAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={invoice.status} />
                    </TableCell>
                    <TableCell>
                      <EInvoiceChip invoice={invoice} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      {/* A draft has not gone out, so it is still anybody's to
                          correct who may edit billing at all. A proforma that
                          has been issued is still only an estimate and is
                          offered to whoever may cancel a bill, money already
                          receipted against it holding the door shut unless a
                          super admin forces it through. A real bill once
                          issued - the tax invoice chief among them - is a
                          filed document everywhere this runs, and correcting
                          one is a super admin's call only, no other authority
                          substituting for it. Either way the change is
                          audited. */}
                      {(invoice.status === 'DRAFT'
                        ? can('billing.edit')
                        : invoice.status !== 'CANCELLED'
                          && (invoice.invoiceType === 'PROFORMA'
                            ? can('billing.cancel') && (invoice.paidAmount === 0 || isSuperAdmin)
                            : isSuperAdmin)) ? (
                        <Tooltip
                          title={
                            invoice.status === 'DRAFT'
                              ? 'Correct this draft'
                              : 'Correct this document. The change is recorded against your name.'
                          }
                        >
                          <IconButton size="small" onClick={() => setEditing(invoice)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {canWrite('billing') && invoice.status === 'DRAFT' ? (
                        <Button size="small" onClick={() => issue.mutate(invoice.id)} disabled={issue.isPending}>
                          Issue
                        </Button>
                      ) : null}
                      {/* Only where an IRN is actually owed: a bill to a guest
                          with no GSTIN is a B2C supply and needs none. */}
                      {invoice.eInvoiceRequired ? (
                        <Button size="small" onClick={() => setEInvoicing(invoice)}>
                          {/* Only an unsigned invoice is still asking for one;
                              a cancelled IRN needs a fresh invoice, not another try. */}
                          {invoice.eInvoiceStatus === 'PENDING'
                          || invoice.eInvoiceStatus === 'FAILED'
                            ? 'Get IRN'
                            : 'IRN'}
                        </Button>
                      ) : null}
                      {can('billing.cancel') && invoice.status !== 'CANCELLED' && invoice.paidAmount === 0 ? (
                        <Button size="small" color="error" onClick={() => setCancelling(invoice)}>
                          Cancel
                        </Button>
                      ) : null}
                      <DocumentActions
                        path={`/api/v1/invoices/${invoice.id}/pdf`}
                        fileName={`${invoice.invoiceNumber.replace(/\//g, '-')}.pdf`}
                        label="invoice"
                      />
                      {/* A proforma and a tax invoice read differently to the
                          customer, so the message says which one arrived. */}
                      <SendDocumentIcon
                        kind={invoice.invoiceType === 'PROFORMA' ? 'proforma-invoice' : 'tax-invoice'}
                        id={invoice.id}
                      />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data && (
              <TablePagination
                component="div"
                count={data.totalElements}
                page={data.page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={size}
                onRowsPerPageChange={(event) => {
                  setSize(Number(event.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 20, 50]}
              />
            )}
          </>
        )}
      </Card>

      <ErrorBanner error={issue.error} />
      <NewInvoiceDialog open={newOpen} onClose={() => setNewOpen(false)} />
      <EditInvoiceDialog invoice={editing} onClose={() => setEditing(null)} />
      <CancelInvoiceDialog invoice={cancelling} onClose={() => setCancelling(null)} />
      <EInvoiceDialog invoice={eInvoicing} onClose={() => setEInvoicing(null)} />
    </>
  )
}
